import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { CustomerVaultsApi } from "@/openapi/generated";
import type { GetCustomerVaultResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface UseVaultGetByIdProps {
  userId: number;
  vaultId: number;
  tenantId: number;
}

export const useVaultGetById = (
  { userId, vaultId, tenantId }: UseVaultGetByIdProps,
  options?: { enabled?: boolean },
) => {
  const accessToken = useAccessToken();

  const { data, isError, isLoading, isSuccess, isPending, refetch, error } =
    useQuery<GetCustomerVaultResponse | null>({
      queryKey: [QueryKeys.CustomerVaults, userId, vaultId],
      queryFn: async () => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }

        const service = createAuthenticatedApi(
          CustomerVaultsApi,
          accessToken,
        );

        const result = await service.apiUsersUserIdVaultsVaultIdGet({
          userId,
          vaultId,
          xTenantID: tenantId,
        });

        return result ?? null;
      },
      enabled:
        options?.enabled !== false &&
        !!accessToken &&
        !!userId &&
        !!vaultId &&
        !!tenantId,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  return {
    data: data ?? null,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
    error,
  };
};
